import React, { useState, useEffect, useRef } from "react";
import "./Sections.css";
import { MdAddCircleOutline, MdErrorOutline } from "react-icons/md";
import { FaRegTrashAlt, FaRegEdit } from "react-icons/fa";
import CustomDropdown from "../Components/CustomDropdown/CustomDropdown";
import UpdateVariantModal from "../Components/UpdateVariantModal/UpdateVariantModal";
import noImage from "../../../../assets/images/noImage.png";

import AsyncAutoComplete from "../../../../Components/AsyncAutoComplete/AsyncAutoComplete";
import { addVariantAttribute } from "../../../../services/product.service";
import { useDispatch } from "react-redux";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import { useTranslation } from "react-i18next";
const VariantSection = ({
  attributes,
  setAttributes,
  variants,
  setVariants,
  formData,
  setFormData,
  errors,
  variationAttributes,
  productSuppliers,
  setVariationAttributes,
  editMode,
}) => {
  const { t } = useTranslation(["inventory", "common"]);
  const [selectedOption, setSelectedOption] = useState("");
  const [timeoutId, setTimeoutId] = useState(null);
  const [selectedOptionValues, setSelectedOptionValues] = useState([""]);
  const [addVariantOption, setAddVariantOption] = useState(false);
  const [editVariantOption, setEditVariantOption] = useState(false);
  const [updateVariantModal, setUpdateVariantModal] = useState(false);
  const [variantErrors, setVariantErrors] = useState();
  const [addManualVariant, setAddManualVariant] = useState(false);
  const [manualVariantData, setManualVariantData] = useState({
    variant: "",
    sku: "",
    code: "",
    regular_price: 0,
    sales_price: 0,
    media: [],
  });
  const userAddingAttribute = useRef(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (userAddingAttribute.current) {
      generateCombinations();
    }
  }, [attributes]);

  const generateCombinations = () => {
    if (attributes.length === 0) {
      setVariants([]);
      return;
    }

    const combinations = generateCombinationsRecursive(attributes, 0);

    // Keep every existing variant (data, id and order) and append only the
    // combinations not already present, so previously added or saved variants
    // are never dropped when attributes change.
    const existingKeys = new Set(variants.map((v) => v.variant));
    const newVariants = combinations
      .filter((comb) => !existingKeys.has(comb))
      .map((comb) => ({
        tempId: Math.random().toString(36).substr(2, 9),
        variant: comb,
        sku: "",
        code: "",
        regular_price: formData.regular_price || 0,
        sales_price: formData.sales_price || 0,
        media: [],
        length: formData.length || "",
        width: formData.width || "",
        height: formData.height || "",
        weight: formData.weight || "",
        dimension_unit: formData.dimension_unit || "",
        weight_unit: formData.weight_unit || "",
        product_identifiers: [],
        suppliers: [],
      }));

    setVariants([...variants, ...newVariants]);
  };

  const addNewOption = async (option) => {
    const response = await addVariantAttribute({ name: option?.title });
    if (!response.error) {
      setVariationAttributes((prevData) => {
        const attr = response?.data?.result;
        return [{ title: attr?.name, value: attr?.id }, ...prevData];
      });
      setSelectedOption({ title: option?.title, value: option?.id });
      dispatch(
        showSnackBar({
          text: t("productForm.optionAddedSuccessfully"),
          severity: "success",
        })
      );
    } else {
      dispatch(
        showSnackBar({
          text: t("productForm.couldntAddNewOption"),
          severity: "error",
        })
      );
    }
  };

  const generateCombinationsRecursive = (
    data,
    currentIndex,
    currentCombination = []
  ) => {
    if (currentIndex === data.length) {
      return [currentCombination.join(" / ")];
    }
    const currentCategory = data[currentIndex];
    const newCombinations = [];

    for (const value of currentCategory?.value) {
      const updatedCombination = [...currentCombination, value];
      const combinationsForNextCategories = generateCombinationsRecursive(
        data,
        currentIndex + 1,
        updatedCombination
      );
      newCombinations.push(...combinationsForNextCategories);
    }

    return newCombinations;
  };

  const handleVariantFieldChange = (index, name, e) => {
    setVariants((vars) =>
      vars.map((variant, idx) => {
        if (index === idx) {
          return { ...variant, [name]: e.target.value };
        } else {
          return variant;
        }
      })
    );
  };
  const handleDeleteAttribute = (index) => {
    userAddingAttribute.current = false;
    if (attributes[index]?.id) {
      setFormData({
        ...formData,
        deletedAttributeIds: [
          ...(Array.isArray(formData?.deletedAttributeIds)
            ? formData.deletedAttributeIds
            : []),
          attributes[index]?.id,
        ],
      });
    }

    setAttributes([...attributes?.filter((atr, idx) => idx !== index)]);
  };
  const handleDeleteVariant = (index) => {
    const variantToRemove = variants[index];
    if (variantToRemove?.id) {
      setFormData((prev) => ({
        ...prev,
        deletedVariationIds: [
          ...(Array.isArray(prev?.deletedVariationIds)
            ? prev.deletedVariationIds
            : []),
          variantToRemove.id,
        ],
      }));
    }
    setVariants((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleAddManualVariant = () => {
    if (!manualVariantData.variant || !manualVariantData.code) {
      dispatch(
        showSnackBar({
          text: t("productForm.variantNameAndCodeAreRequired"),
          severity: "error",
        })
      );
      return;
    }

    const newVariant = {
      tempId: Math.random().toString(36).substr(2, 9),
      variant: manualVariantData.variant,
      sku: manualVariantData.sku,
      code: manualVariantData.code,
      regular_price: parseFloat(manualVariantData.regular_price) || 0,
      sales_price: parseFloat(manualVariantData.sales_price) || 0,
      media: manualVariantData.media,
      length: formData.length || "",
      width: formData.width || "",
      height: formData.height || "",
      weight: formData.weight || "",
      dimension_unit: formData.dimension_unit || "",
      weight_unit: formData.weight_unit || "",
      product_identifiers: [],
      suppliers: [],
    };

    setVariants([...variants, newVariant]);
    setManualVariantData({
      variant: "",
      sku: "",
      code: "",
      regular_price: 0,
      sales_price: 0,
      media: [],
    });
    setAddManualVariant(false);
  };

  const handleManualVariantChange = (field, value) => {
    setManualVariantData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    setFormData({ ...formData, attributes, variants });
  }, [variants]);

  function findDuplicateIndexes(inputArray) {
    const seen = {};
    const duplicates = [];

    for (let i = 0; i < inputArray.length; i++) {
      const value = inputArray[i];
      if (value !== "" && seen[value] !== undefined) {
        duplicates.push(i);
      } else {
        seen[value] = i;
      }
    }

    return duplicates;
  }

  const validateOption = (name, values) => {
    if (!name || name === "") {
      setVariantErrors({
        ...variantErrors,
        optionName: t("productForm.optionNameIsRequired"),
      });
      return false;
    }
    if (values[0] === "") {
      setVariantErrors({
        ...variantErrors,
        firstValue: t("productForm.addAValuePlease"),
      });
      return false;
    }
    setVariantErrors({
      ...variantErrors,
      optionName: false,
      firstValue: false,
    });
    return true;
  };

  useEffect(() => {
    setVariantErrors({
      ...variantErrors,
      duplicateValues: findDuplicateIndexes(selectedOptionValues),
    });
  }, [selectedOptionValues]);

  return (
    <section className="addprod__formsection_wrap" id="addproduct_variants">
      <UpdateVariantModal
        open={updateVariantModal !== false}
        variantIndex={updateVariantModal}
        variant={variants ? variants[updateVariantModal] : {}}
        setOpen={setUpdateVariantModal}
        handleVariantFieldChange={handleVariantFieldChange}
        formData={formData}
        setFormData={setFormData}
        setVariants={setVariants}
        variants={variants ?? []}
        errors={errors}
        productSuppliers={productSuppliers}
        editMode={editMode}
      />
      <div className="addprod__formsection_heading">{t("productForm.variants")}</div>
      <div className="addprod__variants_attributes">
        {attributes?.map((attr, index) =>
          `${index}` !== editVariantOption ? (
            <div className="addprod__variants_attribute" key={index}>
              <div className="addprod__variants_attributetop">
                <h6 className="addprod__variants_attopheading">{attr?.name}</h6>
                <div className="addprod__variants_attopactions">
                  <button className="addprod__variants_attopaction">
                    <FaRegEdit
                      className="addprod__variants_attopactionicon"
                      onClick={() => {
                        setEditVariantOption(`${index}`);
                        setSelectedOption(
                          variationAttributes?.find(
                            (attribute) => attribute.title === attr.name
                          )
                        );
                        setSelectedOptionValues([...attr.value, ""]);
                      }}
                    />
                  </button>
                  <button className="addprod__variants_attopaction">
                    <FaRegTrashAlt
                      className="addprod__variants_attopactionicon"
                      size={14}
                      onClick={() => handleDeleteAttribute(index)}
                    />
                  </button>
                </div>
              </div>
              <div className="addprod__variants_attributbottom">
                {attr?.value?.map((value, index) => (
                  <div
                    key={index}
                    className="addprod__variants_atrbtmvalue"
                    title={value}
                  >
                    {value}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="aprodforms__addvarr_section" key={index}>
              <div className="addprod__custominput_flexrow">
                <div className="addprod__custominput_flexcol">
                  <div className="addprod__custominput_box">
                    <label className="addprod__custominput_title">
                      {t("productForm.optionName")}
                    </label>
                    <CustomDropdown
                      optionObjects={variationAttributes}
                      onSelect={(option) => {
                        setSelectedOption(option);
                      }}
                      selectedOption={selectedOption}
                    />
                    {variantErrors?.optionName && (
                      <div className="addprod__custominputfield_error">
                        <MdErrorOutline className="addprod__custominputfield_erroricn" />
                        {variantErrors?.optionName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="addprod__custominput_flexrow">
                <div className="addprod__custominput_flexcol">
                  <div className="addprod__custominput_box">
                    <label className="addprod__custominput_title">
                      {t("productForm.optionValues")}
                    </label>
                    {selectedOptionValues?.map((value, index) => (
                      <div
                        key={index}
                        className="addprod__custominputfield_cloneable"
                      >
                        <input
                          className="addprod__custominputfield"
                          type="text"
                          placeholder={t("productForm.enterValueNumber", { number: index + 1 })}
                          onChange={(e) => {
                            setSelectedOptionValues([
                              ...selectedOptionValues?.map((value, idx) =>
                                idx === index ? e.target.value : value
                              ),
                            ]);
                            if (index === selectedOptionValues?.length - 1) {
                              if (timeoutId) {
                                clearTimeout(timeoutId);
                              }
                              const newTimeoutId = setTimeout(() => {
                                setSelectedOptionValues((prevval) => [
                                  ...prevval,
                                  "",
                                ]);
                              }, 700);
                              setTimeoutId(newTimeoutId);
                            }
                          }}
                          value={value}
                        />

                        {selectedOptionValues?.length > 1 && (
                          <FaRegTrashAlt
                            size={18}
                            className="addprod__custominputfield_cloneicn"
                            onClick={() =>
                              setSelectedOptionValues([
                                ...selectedOptionValues?.filter(
                                  (value, idx) => idx !== index
                                ),
                              ])
                            }
                          />
                        )}
                      </div>
                    ))}
                    {variantErrors?.duplicateValues?.includes(index) && (
                      <div className="addprod__custominputfield_error">
                        <MdErrorOutline className="addprod__custominputfield_erroricn" />
                        {t("productForm.canNotAddDuplicateValues")}
                      </div>
                    )}
                    {index === 0 && variantErrors?.firstValue && (
                      <div className="addprod__custominputfield_error">
                        <MdErrorOutline className="addprod__custominputfield_erroricn" />
                        {variantErrors?.firstValue}
                      </div>
                    )}
                  </div>
                  <button
                    className="addpaddvar__done_btbcancel"
                    onClick={() => {
                      setEditVariantOption(false);
                      setSelectedOption("");
                      setSelectedOptionValues([""]);
                    }}
                  >
                    {t("common:actions.cancel")}
                  </button>
                  <button
                    className="addpaddvar__done_btb"
                    onClick={() => {
                      const validated = validateOption(
                        selectedOption?.title,
                        selectedOptionValues
                      );
                      if (!validated) {
                        return;
                      }
                      userAddingAttribute.current = true;
                      setAttributes([
                        ...attributes?.map((item, idx) =>
                          `${idx}` === editVariantOption
                            ? {
                                ...item,
                                name: selectedOption?.title,
                                value: selectedOptionValues?.filter(
                                  (item) => item !== ""
                                ),
                              }
                            : item
                        ),
                      ]);
                      setSelectedOption("");
                      setSelectedOptionValues([""]);
                      setEditVariantOption(false);
                    }}
                  >
                    {t("productForm.done")}
                  </button>
                </div>
              </div>
            </div>
          )
        )}
      </div>
      {addVariantOption && (
        <div className="aprodforms__addvarr_section">
          <div className="addprod__custominput_flexrow">
            <div className="addprod__custominput_flexcol">
              <div className="addprod__custominput_box">
                <label className="addprod__custominput_title addprod__custominput_titleflex">
                  <div>{t("productForm.optionName")}</div>
                  {/* <div
                    className="addprod__cusinputitle_addnewopt"
                    onClick={() => setAddVariantAttributeModal(true)}
                  >
                    Add New Option
                  </div> */}
                </label>
                {/* {addVariantOption === "new" ? (
                  <input
                    className="addprod__custominputfield"
                    type="text"
                    placeholder={`Enter New Option Name`}
                    onChange={(e) => {
                      setSelectedOption({
                        title: e.target.value,
                        value: e.target.value.toLowerCase(),
                      });
                    }}
                  />
                ) : (
                  <CustomDropdown
                    optionObjects={variationAttributes}
                    onSelect={(option) => {
                      setSelectedOption(option);
                    }}
                    selectedOption={selectedOption}
                  />
                )} */}
                <AsyncAutoComplete
                  options={variationAttributes}
                  addNewOption={addNewOption}
                  value={selectedOption}
                  setValue={setSelectedOption}
                />
                {variantErrors?.optionName && (
                  <div className="addprod__custominputfield_error">
                    <MdErrorOutline className="addprod__custominputfield_erroricn" />
                    {variantErrors?.optionName}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="addprod__custominput_flexrow">
            <div className="addprod__custominput_flexcol">
              <div className="addprod__custominput_box">
                <label className="addprod__custominput_title">
                  {t("productForm.optionValues")}
                </label>
                {selectedOptionValues?.map((value, index) => (
                  <div
                    key={index}
                    className="addprod__custominputfield_cloneablewrap"
                  >
                    {" "}
                    <div className="addprod__custominputfield_cloneable">
                      <input
                        className={`addprod__custominputfield ${
                          variantErrors?.duplicateValues?.includes(index)
                            ? "addprod__custominputfielderror"
                            : ""
                        }`}
                        type="text"
                        placeholder={t("productForm.enterValueNumber", { number: index + 1 })}
                        onChange={(e) => {
                          setSelectedOptionValues([
                            ...selectedOptionValues?.map((value, idx) =>
                              idx === index ? e.target.value : value
                            ),
                          ]);
                          if (index === selectedOptionValues?.length - 1) {
                            if (timeoutId) {
                              clearTimeout(timeoutId);
                            }
                            const newTimeoutId = setTimeout(() => {
                              setSelectedOptionValues((prevval) => [
                                ...prevval,
                                "",
                              ]);
                            }, 500);
                            setTimeoutId(newTimeoutId);
                          }
                        }}
                        value={value}
                      />

                      {selectedOptionValues?.length > 1 && (
                        <FaRegTrashAlt
                          size={18}
                          className="addprod__custominputfield_cloneicn"
                          onClick={() =>
                            setSelectedOptionValues([
                              ...selectedOptionValues?.filter(
                                (value, idx) => idx !== index
                              ),
                            ])
                          }
                        />
                      )}
                    </div>
                    {variantErrors?.duplicateValues?.includes(index) && (
                      <div className="addprod__custominputfield_error">
                        <MdErrorOutline className="addprod__custominputfield_erroricn" />
                        {t("productForm.canNotAddDuplicateValues")}
                      </div>
                    )}
                    {index === 0 && variantErrors?.firstValue && (
                      <div className="addprod__custominputfield_error">
                        <MdErrorOutline className="addprod__custominputfield_erroricn" />
                        {variantErrors?.firstValue}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button
                className="addpaddvar__done_btbcancel"
                onClick={() => {
                  setAddVariantOption(false);
                  setSelectedOption("");
                  setSelectedOptionValues([""]);
                }}
              >
                {t("common:actions.cancel")}
              </button>
              <button
                className="addpaddvar__done_btb"
                onClick={() => {
                  const validated = validateOption(
                    selectedOption?.title,
                    selectedOptionValues
                  );
                  if (!validated) {
                    return;
                  }
                  setAttributes([
                    ...attributes,
                    {
                      name: selectedOption?.title,
                      value: selectedOptionValues?.filter(
                        (item) => item !== ""
                      ),
                    },
                  ]);
                  setSelectedOption("");
                  setSelectedOptionValues([""]);
                  setAddVariantOption(false);
                }}
              >
                {t("productForm.done")}
              </button>
            </div>
          </div>
        </div>
      )}

      {!addVariantOption && !editVariantOption && attributes?.length < 3 && (
        <button
          className="addprod__formsection_addvarbtn"
          onClick={() => {
            userAddingAttribute.current = true;
            setAddVariantOption(true);
          }}
        >
          <MdAddCircleOutline
            className="addprod__formsection_addvarbtnicn"
            size={14}
          />
          {t("productForm.addVariantOption")}
        </button>
      )}
      {formData?.variants?.length > 0 && (
        <div
          className="variants__combinations_table_wrap"
          style={{ overflowX: "auto", width: "100%" }}
        >
          <table
            className="variants__combinations_table"
            style={{ width: "100%", minWidth: "680px", tableLayout: "fixed" }}
          >
            <thead>
              <tr>
                <td style={{ width: "38px" }}>#</td>
                <td style={{ width: "21%" }}>{t("productForm.name")}</td>
                <td style={{ width: "66px" }}>{t("productForm.images")}</td>
                <td style={{ width: "20%" }}>{t("productForm.sku")}</td>
                <td style={{ width: "18%" }}>{t("productForm.code")}</td>
                <td style={{ width: "96px" }}>{t("productForm.regPrice")}</td>
                <td style={{ width: "92px" }}>{t("productForm.salePrice")}</td>
                <td style={{ width: "88px" }}>{t("productForm.actions")}</td>
              </tr>
            </thead>
            <tbody>
              {formData?.variants?.map((variant, index) => (
                <tr key={variant?.tempId || variant?.id || index}>
                  <td style={{ minWidth: "40px" }}>{index + 1}</td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="text"
                      title={variant?.variant}
                      onChange={(e) =>
                        handleVariantFieldChange(index, "variant", e)
                      }
                      value={variant?.variant}
                      style={{ height: "26px", width: "100%" }}
                    />
                  </td>
                  <td style={{ minWidth: "70px" }}>
                    <div className="flex items-center ">
                      <div className="w-[35px] h-[30px] overflow-hidden rounded-sm cursor-pointer">
                        {variant?.media
                          ?.filter((image) =>
                            [true, "true"].includes(image?.is_primary)
                          )
                          .map((image, index) => (
                            <img
                              key={index}
                              className="w-full h-full object-cover"
                              src={
                                image && image.file
                                  ? image.file
                                  : image.media instanceof Blob
                                  ? URL.createObjectURL(image.media)
                                  : noImage
                              }
                              accept="image/*"
                              alt=""
                            />
                          ))}
                        {(!variant?.media?.length ||
                          variant?.media?.length === 0) && (
                          <img
                            src={noImage}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      {variant?.media?.length > 1 && (
                        <div className="mx-1">
                          <div className="text-[13px] cursor-pointer">
                            +{variant?.media?.length - 1}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="text"
                      placeholder={t("productForm.enterSku")}
                      title={variant.sku}
                      onChange={(e) =>
                        handleVariantFieldChange(index, "sku", e)
                      }
                      value={variant.sku}
                      style={{ height: "26px", width: "100%" }}
                    />
                  </td>
                  <td>
                    <input
                      className={`addprod__custominputfield ${
                        editMode && variant.id ? "disable__click_fade" : ""
                      }`}
                      type="text"
                      placeholder={t("productForm.enterVariantCode")}
                      title={variant.code}
                      onChange={(e) =>
                        handleVariantFieldChange(index, "code", e)
                      }
                      value={variant.code}
                      style={{ height: "26px", width: "100%" }}
                      readOnly={editMode && !!variant.id}
                    />
                  </td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="text"
                      onChange={(e) =>
                        handleVariantFieldChange(index, "regular_price", e)
                      }
                      value={variant.regular_price ?? 0}
                      style={{ height: "26px", width: "100%" }}
                    />
                  </td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="text"
                      onChange={(e) =>
                        handleVariantFieldChange(index, "sales_price", e)
                      }
                      value={variant.sales_price ?? 0}
                      style={{ height: "26px", width: "100%" }}
                    />
                  </td>
                  <td style={{ minWidth: "100px" }}>
                    <div className="addprod__variants_attopactions">
                      <button className="addprod__variants_attopaction">
                        <FaRegEdit
                          className="addprod__variants_attopactionicon"
                          onClick={() => setUpdateVariantModal(index)}
                        />
                      </button>
                      <button className="addprod__variants_attopaction">
                        <FaRegTrashAlt
                          className="addprod__variants_attopactionicon"
                          size={14}
                          onClick={() => handleDeleteVariant(index)}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* Manual Variant Addition Row */}
              {addManualVariant && (
                <tr
                  className="manual-variant-row"
                  style={{ backgroundColor: "var(--color-ui-surface-sunken)" }}
                >
                  <td style={{ minWidth: "40px" }}>+</td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="text"
                      placeholder={t("productForm.variantName")}
                      value={manualVariantData.variant}
                      onChange={(e) =>
                        handleManualVariantChange("variant", e.target.value)
                      }
                      style={{
                        height: "26px",
                        width: "120px",
                      }}
                    />
                  </td>
                  <td style={{ minWidth: "70px" }}>
                    <div className="flex items-center">
                      <div className="w-[35px] h-[30px] overflow-hidden rounded-sm cursor-pointer">
                        <img
                          src={noImage}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="text"
                      placeholder={t("productForm.sku")}
                      value={manualVariantData.sku}
                      onChange={(e) =>
                        handleManualVariantChange("sku", e.target.value)
                      }
                      style={{
                        height: "26px",
                        width: "100px",
                      }}
                    />
                  </td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="text"
                      placeholder={t("productForm.code")}
                      value={manualVariantData.code}
                      onChange={(e) =>
                        handleManualVariantChange("code", e.target.value)
                      }
                      style={{
                        height: "26px",
                        width: "80px",
                      }}
                    />
                  </td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="number"
                      placeholder="0.00"
                      value={manualVariantData.regular_price}
                      onChange={(e) =>
                        handleManualVariantChange(
                          "regular_price",
                          e.target.value
                        )
                      }
                      style={{
                        height: "26px",
                        width: "80px",
                      }}
                    />
                  </td>
                  <td>
                    <input
                      className="addprod__custominputfield"
                      type="number"
                      placeholder="0.00"
                      value={manualVariantData.sales_price}
                      onChange={(e) =>
                        handleManualVariantChange("sales_price", e.target.value)
                      }
                      style={{
                        height: "26px",
                        width: "80px",
                      }}
                    />
                  </td>
                  <td style={{ minWidth: "100px" }}>
                    <div className="addprod__variants_attopactions">
                      <button
                        className="addprod__variants_attopaction"
                        onClick={handleAddManualVariant}
                        style={{
                          backgroundColor: "#28a745",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                        }}
                      >
                        ✓
                      </button>
                      <button
                        className="addprod__variants_attopaction"
                        onClick={() => {
                          setAddManualVariant(false);
                          setManualVariantData({
                            variant: "",
                            sku: "",
                            code: "",
                            regular_price: 0,
                            sales_price: 0,
                            media: [],
                          });
                        }}
                        style={{
                          backgroundColor: "#dc3545",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Manual Variant Button */}
      {formData?.variants?.length > 0 && !addManualVariant && (
        <div
          className="addprod__manual_variant_section"
          style={{ marginTop: "15px" }}
        >
          <button
            className="addprod__formsection_addvarbtn"
            onClick={() => setAddManualVariant(true)}
            style={{
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              padding: "6px 12px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <MdAddCircleOutline size={12} />
            {t("productForm.addSingleVariant")}
          </button>
        </div>
      )}
    </section>
  );
};

export default VariantSection;
